import { Link } from 'react-router-dom';
import type { Dish, Language } from '../types';
import { Chip, dayName as dayNameOf } from './ui';

const AUDIENCE_LABEL: Record<string, { en: string; fr: string }> = {
  everyone: { en: 'Everyone', fr: 'Tout le monde' },
  kids: { en: 'Kids', fr: 'Enfants' },
  adults: { en: 'Adults', fr: 'Adultes' },
};

const T = {
  en: {
    howToCook: 'How to cook',
    swap: 'Surprise me',
    swapTitle: 'Let the planner pick something else for this day',
    choose: 'Choose',
    chooseTitle: 'Pick the meal yourself, favourites first',
    keep: 'Keep',
    keeping: 'Keeping',
    keepTitle: 'Keep this meal when the week is re-planned',
    keptTitle: 'This meal will not change when you re-plan. Tap to release it.',
    quick: 'Quick',
    regular: 'Regular',
    fish: 'Fish',
    keto: 'Keto',
    baked: 'Baking afternoon',
    bought: 'Shop-bought',
    invented: 'New idea',
    move: 'Move this meal',
    moveTitle: 'Drag this onto another day to swap them over — or tap it, then tap the other one',
    holding: 'Pick the meal to swap it with',
    inventedTitle:
      'Kitja wrote this one: the library had nothing that worked for this sitting. Swap it or say no and it will not come back.',
    nothingYet: 'Nothing planned yet',
    min: 'min',
    favourite: 'Favourite',
    isFavourite: 'Favourite ✓',
    favouriteTitle: 'Mark this as a family favourite — it will come round more often',
    unfavouriteTitle: 'Remove from family favourites',
    kept: 'Kept',
    cooking: 'Cooking',
    batchDouble: 'Cook double',
    batchDoubleTitle: 'Make extra and serve it again later in the week',
    batchFeeds: (day: string, where: string) => `Making extra for ${day} · ${where}`,
    batchFrom: (day: string) => `From ${day}’s batch — nothing to cook`,
    batchUndo: 'Cook it fresh instead',
    fridge: 'fridge',
    freezer: 'freezer',
    nobodyCooking: 'Who’s cooking?',
    cookTitle: 'Who is making this — it carries over to next week',
    bought2: 'Bought',
    boughtTitle: 'Everything for this meal is already in the house',
    waiting: (items: string[], shop: string) =>
      `${items.join(', ')} still to buy at the ${shop}`,
    cooked: 'Cooked',
  },
  fr: {
    howToCook: 'Comment cuisiner',
    swap: 'Surprends-moi',
    swapTitle: 'Laisser le planificateur proposer autre chose',
    choose: 'Choisir',
    chooseTitle: 'Choisir le plat vous-même, favoris en premier',
    keep: 'Garder',
    keeping: 'Gardé',
    keepTitle: 'Garder ce plat lors de la replanification',
    keptTitle: 'Ce plat ne changera pas. Touchez pour le libérer.',
    quick: 'Rapide',
    regular: 'Normal',
    fish: 'Poisson',
    keto: 'Keto',
    baked: 'On pâtisse',
    bought: 'Acheté',
    invented: 'Nouveauté',
    move: 'Déplacer ce plat',
    moveTitle:
      'Faites-le glisser sur un autre jour pour les échanger — ou touchez-le, puis touchez l’autre',
    holding: 'Choisissez le plat à échanger',
    inventedTitle:
      "Kitja a composé ce plat : rien dans la bibliothèque ne convenait à ce repas. Changez-le ou refusez-le et il ne reviendra pas.",
    nothingYet: 'Rien de prévu',
    min: 'min',
    favourite: 'Favori',
    isFavourite: 'Favori ✓',
    favouriteTitle: 'Marquer comme favori de la famille — il reviendra plus souvent',
    unfavouriteTitle: 'Retirer des favoris',
    kept: 'Gardé',
    cooking: 'Aux fourneaux',
    batchDouble: 'Doubler',
    batchDoubleTitle: 'Préparer plus et resservir plus tard dans la semaine',
    batchFeeds: (day: string, where: string) => `Portions en plus pour ${day} · ${where}`,
    batchFrom: (day: string) => `Préparé le ${day} — rien à cuisiner`,
    batchUndo: 'Cuisiner frais à la place',
    fridge: 'réfrigérateur',
    freezer: 'congélateur',
    nobodyCooking: 'Qui cuisine ?',
    cookTitle: 'Qui prépare ce repas — reporté sur la semaine suivante',
    bought2: 'Acheté',
    boughtTitle: 'Tout ce qu’il faut pour ce plat est déjà à la maison',
    waiting: (items: string[], shop: string) =>
      `${items.join(', ')} — encore à acheter (${shop})`,
    cooked: 'Cuisiné',
  },
} as const;

/**
 * The meal card from §33 — it exists to answer one question: "do we fancy this?"
 *
 * Actions are deliberately named for what they do to the week rather than in
 * app jargon: you *swap* a meal you do not fancy, and *keep* one you do so the
 * planner leaves it alone. A kept meal is obvious at a glance.
 */
export function DishCard({
  dish,
  language,
  showAudience,
  busy,
  onReroll,
  onChoose,
  onLock,
  onFavourite,
  onSetCook,
  onBatch,
  onUnbatch,
  cooks,
  grip,
  held,
}: {
  dish: Dish;
  language: Language;
  showAudience: boolean;
  busy?: boolean;
  onReroll?: (dish: Dish) => void;
  onChoose?: (dish: Dish) => void;
  onLock?: (dish: Dish) => void;
  onFavourite?: (dish: Dish) => void;
  onSetCook?: (dish: Dish, memberId: string | null) => void;
  onBatch?: (dish: Dish) => void;
  onUnbatch?: (dish: Dish) => void;
  /** Everyone who could reasonably be cooking. */
  cooks?: Array<{ id: string; name: string }>;
  /**
   * Handlers that make this card draggable onto another day (§34). Absent
   * where a meal cannot move — a day that has gone, a kept meal, the kitchen
   * tablet's read-only view.
   */
  grip?: Record<string, unknown>;
  /** This is the meal currently being carried. */
  held?: boolean;
}) {
  const t = T[language];
  const meal = dish.meal;
  const isSnack = meal?.course === 'morning_snack';

  return (
    <div
      className={`dish${dish.locked ? ' kept' : ''}${
        dish.supply.status === 'settled' ? ' settled' : ''
      }`}
    >
      <div className="dish-top">
        {grip && meal && (
          <button
            className="dish-grip"
            type="button"
            title={held ? t.holding : t.moveTitle}
            aria-label={t.move}
            aria-pressed={held}
            {...grip}
          >
            ⠿
          </button>
        )}
        <div className="dish-main">
          {showAudience && (
            <div className="audience-tag">
              {AUDIENCE_LABEL[dish.audience]?.[language] ?? dish.audience}
              {dish.dietMode === 'keto' ? ' · Keto' : ''}
            </div>
          )}

          {!meal ? (
            <h3 className="muted">{t.nothingYet}</h3>
          ) : (
            <>
              <h3>
                {dish.locked && (
                  <span className="kept-mark" aria-label={t.kept} title={t.keptTitle}>
                    ✓
                  </span>
                )}
                {meal.name}
                {meal.isFavourite && <span aria-hidden="true"> ♥</span>}
              </h3>
              {meal.description && <p className="desc">{meal.description}</p>}

              <div className="chips">
                {/* A snack box needs no effort rating; a dinner does. */}
                {!isSnack && <Chip>{meal.prepType === 'quick' ? t.quick : t.regular}</Chip>}
                {meal.totalMinutes > 0 && (
                  <Chip>
                    {meal.totalMinutes} {t.min}
                  </Chip>
                )}
                {meal.isFish && <Chip kind="fish">{t.fish}</Chip>}
                {dish.dietMode === 'keto' && <Chip kind="keto">{t.keto}</Chip>}
                {meal.source === 'invented' && (
                  <Chip kind="accent" title={t.inventedTitle}>
                    ✨ {t.invented}
                  </Chip>
                )}
                {meal.gouterKind === 'baked' && <Chip kind="fav">{t.baked}</Chip>}
                {meal.gouterKind === 'bought' && <Chip>{t.bought}</Chip>}
                {meal.isRegular && <Chip kind="accent">Regular</Chip>}
                {dish.locked && <Chip kind="accent">✓ {t.kept}</Chip>}
                {/* What the shopping has to say about this dish (§43-44). */}
                {/* On the kitchen view there is no picker, but who is cooking
                    is one of the two things anybody standing there wants. */}
                {dish.cook && !onSetCook && <Chip kind="accent">👩‍🍳 {dish.cook.name}</Chip>}
                {dish.batch?.role === 'reheating' && (
                  <Chip kind="accent">
                    {dish.batch.storage === 'freezer' ? '❄' : '🧊'}{' '}
                    {t.batchFrom(dayNameOf(dish.batch.otherDate, language))}
                  </Chip>
                )}
                {dish.batch?.role === 'cooking' && (
                  <Chip kind="fav">
                    🍲{' '}
                    {t.batchFeeds(
                      dayNameOf(dish.batch.otherDate, language),
                      dish.batch.storage === 'freezer' ? t.freezer : t.fridge,
                    )}
                  </Chip>
                )}
                {dish.supply.status === 'cooked' && <Chip>{t.cooked}</Chip>}
                {dish.supply.status === 'settled' && (
                  <Chip kind="accent" title={t.boughtTitle}>
                    🛒 {t.bought2}
                  </Chip>
                )}
              </div>

              {/* The butcher on Thursday case: the meal is otherwise settled,
                  but one item has not been bought yet, so it is still open. */}
              {dish.supply.awaiting.length > 0 && (
                <p className="awaiting">
                  {t.waiting(
                    dish.supply.awaiting.map((a) => a.ingredient),
                    dish.supply.awaiting[0]!.shopLabel,
                  )}
                </p>
              )}

              {/* Named once, then carried week to week — so this is normally
                  read, not set. */}
              {onSetCook && cooks && dish.plannedMealId && (
                <label className="cook-row" title={t.cookTitle}>
                  <span className="dim">{t.cooking}</span>
                  <select
                    value={dish.cook?.id ?? ''}
                    disabled={busy}
                    onChange={(event) => onSetCook(dish, event.target.value || null)}
                  >
                    <option value="">{t.nobodyCooking}</option>
                    {cooks.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <div className="actions">
                {dish.plannedMealId && meal.totalMinutes > 0 && (
                  <Link className="btn primary" to={`/cook/${dish.plannedMealId}`}>
                    {t.howToCook}
                  </Link>
                )}
                {onBatch && !dish.batch && meal.totalMinutes > 0 && (
                  <button
                    className="btn"
                    onClick={() => onBatch(dish)}
                    disabled={busy || dish.locked}
                    title={t.batchDoubleTitle}
                  >
                    🍲 {t.batchDouble}
                  </button>
                )}
                {onUnbatch && dish.batch?.role === 'reheating' && (
                  <button className="btn" onClick={() => onUnbatch(dish)} disabled={busy}>
                    {t.batchUndo}
                  </button>
                )}
                {onChoose && !dish.batch && (
                  <button
                    className="btn"
                    onClick={() => onChoose(dish)}
                    disabled={busy || dish.locked}
                    title={dish.locked ? t.keptTitle : t.chooseTitle}
                  >
                    ☰ {t.choose}
                  </button>
                )}
                {onReroll && !dish.batch && (
                  <button
                    className="btn"
                    onClick={() => onReroll(dish)}
                    disabled={busy || dish.locked}
                    title={dish.locked ? t.keptTitle : t.swapTitle}
                  >
                    ↻ {t.swap}
                  </button>
                )}
                {onLock && (
                  <button
                    className={`btn${dish.locked ? ' on' : ''}`}
                    onClick={() => onLock(dish)}
                    disabled={busy}
                    aria-pressed={dish.locked}
                    title={dish.locked ? t.keptTitle : t.keepTitle}
                  >
                    {dish.locked ? `✓ ${t.keeping}` : t.keep}
                  </button>
                )}
                {onFavourite && (
                  <button
                    className={`btn${meal.isFavourite ? ' on' : ''}`}
                    onClick={() => onFavourite(dish)}
                    disabled={busy}
                    title={meal.isFavourite ? t.unfavouriteTitle : t.favouriteTitle}
                    aria-pressed={meal.isFavourite}
                  >
                    ♥ {meal.isFavourite ? t.isFavourite : t.favourite}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
